import React from 'react';
import { Star, Trash2, Percent, Upload, Copy } from 'lucide-react';
import ProductImageGallery from './ProductImageGallery';
import EditableText from './EditableText';
import RichTextField from './RichTextField';
import DatasheetUpload from './DatasheetUpload';
import ReviewManager from './ReviewManager';
import ProTipsEditor from './ProTipsEditor';
import AttributesEditor from './AttributesEditor';
import VariantsEditor from './VariantsEditor';
import LinkedRecommendationsEditor from './LinkedRecommendationsEditor';
import ShippingSlabsEditor from './ShippingSlabsEditor';

/**
 * Inline product editing card used inside the Admin > Products accordion.
 * All edits are persisted immediately via the passed `updateProduct` handler.
 */
const ProductAdminCard = ({ p, categories, updateProduct, deleteProduct, duplicateProduct }) => (
  <div className="bg-white border border-neutral-200 rounded-sm p-4">
    <ProductImageGallery
      primary={p.image}
      extras={p.images || []}
      onChange={({ image, images }) => updateProduct(p.id, { image, images })}
    />
    <div className="space-y-2 text-sm mt-3">
      <div className="flex gap-3">
        <div className="flex-1"><span className="text-[11px] uppercase text-[#F46A25] font-bold flex items-center gap-1"><Star className="w-3 h-3 fill-[#F46A25]" /> SKU (internal)</span>
          <EditableText value={p.sku} onSave={(v) => updateProduct(p.id, { sku: v })} className="font-mono font-bold text-[#12263A]" /></div>
        <div className="flex-1"><span className="text-[11px] uppercase text-[#F46A25] font-bold">Dr Tools Part No</span>
          <EditableText value={p.part_no} onSave={(v) => updateProduct(p.id, { part_no: v || null })} placeholder="DR-0001" className="font-mono text-[#12263A]" /></div>
        <div className="flex-1"><span className="text-[11px] uppercase text-neutral-500 font-semibold">Brand</span>
          <EditableText value={p.brand} onSave={(v) => updateProduct(p.id, { brand: v })} /></div>
      </div>
      <div><span className="text-[11px] uppercase text-neutral-500 font-semibold">Name</span>
        <EditableText value={p.name} onSave={(v) => updateProduct(p.id, { name: v })} className="font-semibold" /></div>
      <div className="flex gap-3">
        <div className="flex-1"><span className="text-[11px] uppercase text-neutral-500 font-semibold">Price</span>
          <EditableText value={p.price} type="number" prefix="£" onSave={(v) => updateProduct(p.id, { price: v })} className="font-bold" /></div>
        <div className="w-24"><span className="text-[11px] uppercase text-neutral-500 font-semibold">Unit</span>
          <EditableText value={p.unit} onSave={(v) => updateProduct(p.id, { unit: v || null })} placeholder="each / box / SQM" /></div>
        <div className="flex-1"><span className="text-[11px] uppercase text-neutral-500 font-semibold">Was price</span>
          <EditableText value={p.wasPrice} type="number" prefix="£" onSave={(v) => updateProduct(p.id, { wasPrice: v })} /></div>
      </div>
      <div className="flex gap-3">
        <div className="flex-1">
          <span className="text-[11px] uppercase text-neutral-500 font-semibold">Category</span>
          <select
            value={p.category}
            onChange={(e) => updateProduct(p.id, { category: e.target.value })}
            className="block w-full mt-1 border border-neutral-300 rounded-sm px-2 py-1 text-sm focus:outline-none focus:border-[#F46A25]"
          >
            {categories.filter(c => !c.parent_id).map(cat => {
              const subs = categories.filter(s => s.parent_id === cat.id);
              return (
                <optgroup key={cat.id} label={cat.name}>
                  <option value={cat.slug}>{cat.name}</option>
                  {subs.map(sub => (
                    <option key={sub.id} value={sub.slug}>{'\u00A0\u00A0\u21B3 ' + sub.name}</option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </div>
        <div className="flex-1"><span className="text-[11px] uppercase text-neutral-500 font-semibold">Stock</span>
          <EditableText value={p.stock} type="number" onSave={(v) => updateProduct(p.id, { stock: v })} /></div>
      </div>
      <div><span className="text-[11px] uppercase text-neutral-500 font-semibold">Description</span>
        <RichTextField value={p.description} onSave={(v) => updateProduct(p.id, { description: v })} rows={20} /></div>

      <div className="flex gap-3">
        <div className="flex-1"><span className="text-[11px] uppercase text-neutral-500 font-semibold">Size</span>
          <EditableText value={p.size} onSave={(v) => updateProduct(p.id, { size: v || null })} placeholder="e.g. 300mm" /></div>
        <div className="flex-1"><span className="text-[11px] uppercase text-neutral-500 font-semibold">Details</span>
          <EditableText value={p.others} onSave={(v) => updateProduct(p.id, { others: v || null })} placeholder="e.g. Colour: Black" /></div>
      </div>

      <div className="border-t border-neutral-200 pt-2 mt-2">
        <div className="text-[11px] uppercase text-[#F46A25] font-bold mb-1 flex items-center gap-1"><Upload className="w-3 h-3" /> Datasheet (PDF)</div>
        <DatasheetUpload
          src={p.datasheet_url}
          onChange={(url) => updateProduct(p.id, { datasheet_url: url })}
        />
      </div>

      <div className="border-t border-neutral-200 pt-2 mt-2">
        <div className="text-[11px] uppercase text-[#F46A25] font-bold mb-1">Flooring / boxed product</div>
        <div className="flex gap-3">
          <div className="flex-1">
            <span className="text-[11px] uppercase text-neutral-500 font-semibold">Coverage per box (sqm)</span>
            <EditableText
              value={p.coverage_per_box_sqm}
              type="number"
              onSave={(v) => updateProduct(p.id, { coverage_per_box_sqm: v || null })}
              placeholder="Blank = not flooring"
              data-testid={`coverage-per-box-${p.id}`}
            />
          </div>
          <div className="flex-1 pt-4">
            <div className="text-[10px] text-neutral-500 leading-snug">
              {p.coverage_per_box_sqm > 0 ? (
                <>Derived: <b>£{(p.price / p.coverage_per_box_sqm).toFixed(2)}</b> per sqm.<br/>Calculator appears on the product page.</>
              ) : (
                <>Set this to enable the area calculator + "£/sqm" secondary price on the product page.</>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-neutral-200 pt-2 mt-2">
        <div className="text-[11px] uppercase text-[#F46A25] font-bold mb-1">Bulk buy</div>
        <div className="flex gap-3">
          <div className="flex-1"><span className="text-[11px] uppercase text-neutral-500 font-semibold">Min qty</span>
            <EditableText value={p.bulk_qty} type="number" onSave={(v) => updateProduct(p.id, { bulk_qty: v || null })} placeholder="Off" /></div>
          <div className="flex-1"><span className="text-[11px] uppercase text-neutral-500 font-semibold">Bulk price/unit</span>
            <EditableText value={p.bulk_price} type="number" prefix="£" onSave={(v) => updateProduct(p.id, { bulk_price: v || null })} placeholder="Off" /></div>
        </div>
      </div>

      <div className="border-t border-neutral-200 pt-2 mt-2">
        <div className="text-[11px] uppercase text-[#F46A25] font-bold mb-1">Shipping overrides <span className="text-[10px] text-neutral-500 font-normal normal-case">(blank = use global £75 free-delivery rule)</span></div>
        <div className="flex gap-3">
          <div className="flex-1">
            <span className="text-[11px] uppercase text-neutral-500 font-semibold">Free delivery from qty</span>
            <EditableText
              value={p.free_delivery_qty}
              type="number"
              onSave={(v) => updateProduct(p.id, { free_delivery_qty: v || null })}
              placeholder="Off"
              data-testid={`free-delivery-qty-${p.id}`}
            />
          </div>
          <div className="flex-1">
            <span className="text-[11px] uppercase text-neutral-500 font-semibold">Individual delivery / unit</span>
            <EditableText
              value={p.individual_delivery_cost}
              type="number"
              prefix="£"
              onSave={(v) => updateProduct(p.id, { individual_delivery_cost: v || null })}
              placeholder="Off"
              data-testid={`individual-delivery-cost-${p.id}`}
            />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 mt-2">
          <div><span className="text-[10px] uppercase text-neutral-500 font-semibold block">Weight (kg)</span>
            <EditableText value={p.weight_kg} type="number" onSave={(v) => updateProduct(p.id, { weight_kg: v || null })} placeholder="—" data-testid={`weight-kg-${p.id}`} /></div>
          <div><span className="text-[10px] uppercase text-neutral-500 font-semibold block">L (cm)</span>
            <EditableText value={p.length_cm} type="number" onSave={(v) => updateProduct(p.id, { length_cm: v || null })} placeholder="—" data-testid={`length-cm-${p.id}`} /></div>
          <div><span className="text-[10px] uppercase text-neutral-500 font-semibold block">W (cm)</span>
            <EditableText value={p.width_cm} type="number" onSave={(v) => updateProduct(p.id, { width_cm: v || null })} placeholder="—" data-testid={`width-cm-${p.id}`} /></div>
          <div><span className="text-[10px] uppercase text-neutral-500 font-semibold block">H (cm)</span>
            <EditableText value={p.height_cm} type="number" onSave={(v) => updateProduct(p.id, { height_cm: v || null })} placeholder="—" data-testid={`height-cm-${p.id}`} /></div>
        </div>
        <ShippingSlabsEditor
          product={p}
          onSave={(slabs) => updateProduct(p.id, { shipping_slabs: slabs })}
        />
      </div>

      <div className="border-t border-neutral-200 pt-2">
        <div className="text-[11px] uppercase text-[#F46A25] font-bold mb-1 flex items-center gap-1"><Percent className="w-3 h-3" /> Group pricing — Discount % or Manual £</div>
        <div className="text-[10px] text-neutral-500 mb-2 normal-case">Leave the manual price blank to use the discount %. Filling in a manual price overrides the % for that tier.</div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <span className="text-[10px] uppercase text-neutral-500 font-semibold block">End user</span>
            <div className="flex items-center gap-1">
              <EditableText value={p.discount_end_user || 0} type="number" onSave={(v) => updateProduct(p.id, { discount_end_user: v || 0 })} className="font-semibold" data-testid={`discount-end-user-${p.id}`} />
              <span className="text-[10px] text-neutral-500">%</span>
            </div>
            <EditableText value={p.manual_price_end_user} type="number" prefix="£" onSave={(v) => updateProduct(p.id, { manual_price_end_user: v || null })} placeholder="Manual" data-testid={`manual-price-end-user-${p.id}`} />
          </div>
          <div>
            <span className="text-[10px] uppercase text-neutral-500 font-semibold block">Builder</span>
            <div className="flex items-center gap-1">
              <EditableText value={p.discount_builder || 0} type="number" onSave={(v) => updateProduct(p.id, { discount_builder: v || 0 })} className="font-semibold" data-testid={`discount-builder-${p.id}`} />
              <span className="text-[10px] text-neutral-500">%</span>
            </div>
            <EditableText value={p.manual_price_builder} type="number" prefix="£" onSave={(v) => updateProduct(p.id, { manual_price_builder: v || null })} placeholder="Manual" data-testid={`manual-price-builder-${p.id}`} />
          </div>
          <div>
            <span className="text-[10px] uppercase text-neutral-500 font-semibold block">Retailer</span>
            <div className="flex items-center gap-1">
              <EditableText value={p.discount_retailer || 0} type="number" onSave={(v) => updateProduct(p.id, { discount_retailer: v || 0 })} className="font-semibold" data-testid={`discount-retailer-${p.id}`} />
              <span className="text-[10px] text-neutral-500">%</span>
            </div>
            <EditableText value={p.manual_price_retailer} type="number" prefix="£" onSave={(v) => updateProduct(p.id, { manual_price_retailer: v || null })} placeholder="Manual" data-testid={`manual-price-retailer-${p.id}`} />
          </div>
        </div>
      </div>

      <div className="border-t border-neutral-200 pt-2">
        <div className="text-[11px] uppercase text-[#F46A25] font-bold mb-1">Collection price ("I will collect") <span className="text-[10px] text-neutral-500 font-normal normal-case">— blank = hide toggle for this product</span></div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <span className="text-[10px] uppercase text-neutral-500 font-semibold block">End user</span>
            <EditableText value={p.collection_price_end_user} type="number" prefix="£" onSave={(v) => updateProduct(p.id, { collection_price_end_user: v || null })} placeholder="Off" data-testid={`collection-price-end-user-${p.id}`} />
          </div>
          <div>
            <span className="text-[10px] uppercase text-neutral-500 font-semibold block">Builder</span>
            <EditableText value={p.collection_price_builder} type="number" prefix="£" onSave={(v) => updateProduct(p.id, { collection_price_builder: v || null })} placeholder="Off" data-testid={`collection-price-builder-${p.id}`} />
          </div>
          <div>
            <span className="text-[10px] uppercase text-neutral-500 font-semibold block">Retailer</span>
            <EditableText value={p.collection_price_retailer} type="number" prefix="£" onSave={(v) => updateProduct(p.id, { collection_price_retailer: v || null })} placeholder="Off" data-testid={`collection-price-retailer-${p.id}`} />
          </div>
        </div>
      </div>

      <VariantsEditor product={p} onSave={(variants) => updateProduct(p.id, { variants })} />
      <LinkedRecommendationsEditor product={p} onSave={(updates) => updateProduct(p.id, updates)} />
      <ReviewManager productId={p.id} />
      <ProTipsEditor product={p} updateProduct={updateProduct} />
      <AttributesEditor product={p} updateProduct={updateProduct} />
    </div>
    <div className="mt-3 grid grid-cols-2 gap-2">
      <button
        onClick={() => duplicateProduct?.(p.id)}
        data-testid={`product-duplicate-${p.id}`}
        className="text-[#12263A] hover:bg-orange-50 hover:text-[#F46A25] border border-neutral-300 hover:border-[#F46A25] text-xs font-semibold py-1.5 rounded-sm flex items-center justify-center gap-1"
        title="Clone this product into a new SKU/Part No"
      >
        <Copy className="w-3 h-3" /> Duplicate
      </button>
      <button onClick={() => deleteProduct(p.id)} className="text-red-600 hover:bg-red-50 border border-red-200 text-xs font-semibold py-1.5 rounded-sm flex items-center justify-center gap-1">
        <Trash2 className="w-3 h-3" /> Delete
      </button>
    </div>
  </div>
);

export default ProductAdminCard;
